import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import { PAGE_HEADER_HEIGHT } from '../constants/layout';
import { useDarkMode } from '../context/DarkModeContext';
import { DocumentIcon, ListBulletIcon, Squares2X2Icon, ArrowDownTrayIcon, TrashIcon, EyeIcon, XMarkIcon, ClockIcon, DocumentTextIcon, FolderIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import * as pdfjsLib from 'pdfjs-dist';
import FileViewerModal from '../components/FileViewerModal';
import { useSettings } from '../context/SettingsContext';
import { FileMetadata, FileUsageType, FileUsageContext } from '../types/files';
import { ModernFileService } from '../utils/modernFileService';
import { createLogger } from '../utils/logger';
import { useBannerAutoRead } from '../hooks/useBannerAutoRead';
import ThumbnailPreview from '../components/ThumbnailPreview';
import StorageQuotaDisplay from '../components/StorageQuotaDisplay';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import useBottomUrlBarDetection from '../hooks/useBottomUrlBarDetection';
import useTouchScroll from '../hooks/useTouchScroll';
import { useLocation } from 'react-router-dom';
import useUserTimezone from '../hooks/useUserTimezone';

// Initialize logger
const logger = createLogger({ module: 'Files' });

// Initialize file service
const fileService = ModernFileService.getInstance();

// Move the PDF.js worker setup outside the component
// Configure PDF.js for version 5.x
if (typeof window !== 'undefined') {
  // Use a static path for the worker to avoid file system operations
  // The worker file should be copied to public directory during build
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
}

// Types
interface FileWithThumbnail extends Omit<FileMetadata, 'usage' | 'source'> {
  usage: {
    type: FileUsageType;
    context: FileUsageContext;
    work_experience_id?: string;
  };
  source: 'local' | 'google_drive';
  thumbnailUrl?: string;
}

// Add these types after existing interfaces
type SortField = 'created_at' | 'mime_type' | 'size' | 'usage' | 'file_name';
type SortOrder = 'asc' | 'desc';

// Add this constant at the top of the file with other types/interfaces
const SORT_OPTIONS = [
  { value: 'file_name', label: 'Name' },
  { value: 'created_at', label: 'Date' },
  { value: 'mime_type', label: 'Type' },
  { value: 'size', label: 'File Size' },
  { value: 'usage', label: 'Usage' },
] as const;

// Add this helper function
const getSortLabel = (field: SortField): string => {
  return SORT_OPTIONS.find(option => option.value === field)?.label || field;
};

// Add this interface for the props
interface FilesProps {
  initialFiles?: FileMetadata[];
  initialQuotaInfo?: {
    usagePercentage: number;
    usedSize: number;
    totalSize: number;
  } | null;
}

// Add persistent cache key for files
const FILES_CACHE_KEY = 'career_card_files_cache';
const QUOTA_CACHE_KEY = 'career_card_quota_cache';
const METADATA_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Add debounce utility
const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<F>): void => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };
};

// Move utility functions outside the component to avoid recreation
const formatDate = (dateString: string, timezone?: string): string => {
  if (!dateString) return '';
  
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Simplify the Files component to prevent infinite update loops
const Files: React.FC<FilesProps> = ({ initialFiles = [], initialQuotaInfo = null }) => {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { settings, updateSettings, isOffline } = useSettings();
  const { formatDateOnlyWithTimezone } = useUserTimezone();
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  const location = useLocation();
  
  // Auto-mark banner notifications as read when landing on files page
  useBannerAutoRead();
  const [viewType, setViewType] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [files, setFiles] = useState<FileWithThumbnail[]>([]);
  const [loading, setLoading] = useState(!initialFiles.length);
  const [showLoading, setShowLoading] = useState(false);
  const loadingTimerRef = useRef<number | null>(null);
  const [selectedResume, setSelectedResume] = useState<FileWithThumbnail | null>(null);
  const [selectedResumeUrl, setSelectedResumeUrl] = useState<string | null>(null);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [selectedFileForInfo, setSelectedFileForInfo] = useState<FileWithThumbnail | null>(null);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [focusedFileId, setFocusedFileId] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<{
    usagePercentage: number;
    usedSize: number;
    totalSize: number;
  } | null>(initialQuotaInfo);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [showQuotaLoading, setShowQuotaLoading] = useState(false);
  const quotaLoadingTimerRef = useRef<number | null>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const fileRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const journeyRef = useRef<string | null>(null);
  const hasInitialSSRData = useRef<boolean>(initialFiles.length > 0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const lastFetchTime = useRef<number>(0);
  const MIN_FETCH_INTERVAL = 5000; // 5 seconds between fetches
  const lastFilesResponse = useRef<FileWithThumbnail[]>([]);
  const isDataFreshRef = useRef<boolean>(false);
  const didInitialFetch = useRef<boolean>(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentFilesLength = useRef<number>(0);
  const filesForCleanup = useRef<FileWithThumbnail[]>([]);
  
  // Handle location state for file opening from timeline
  const { openFileId, shouldOpenFile } = location.state || {};
  const processedFileIdRef = useRef<string | null>(null);

  // Add ref for scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Apply touch scroll handling
  useTouchScroll(scrollContainerRef);

  // Memoized debounce function that won't recreate
  const debouncedSearch = useMemo(() => {
    return debounce((query: string) => {
      setDebouncedSearchQuery(query);
    }, 500);
  }, []);

  // Simple handler for search input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Load cached files from localStorage
  const loadCachedFiles = useCallback(() => {
    try {
      const cachedFilesData = localStorage.getItem(FILES_CACHE_KEY);
      if (cachedFilesData) {
        const { files: cachedFiles, timestamp } = JSON.parse(cachedFilesData);
        const now = Date.now();
        if (now - timestamp < METADATA_CACHE_TTL && Array.isArray(cachedFiles)) {
          logger.debug('Using cached files from localStorage');
          setFiles(cachedFiles);
          lastFilesResponse.current = cachedFiles;
          return true;
        }
      }
    } catch (error) {
      logger.error('Error loading cached files:', error);
      try { localStorage.removeItem(FILES_CACHE_KEY); } catch (e) {}
    }
    return false;
  }, []);

  // Save files to localStorage
  const saveFilesToCache = useCallback((filesToCache: FileWithThumbnail[]) => {
    if (!filesToCache || !Array.isArray(filesToCache)) return;
    try {
      localStorage.setItem(FILES_CACHE_KEY, JSON.stringify({
        files: filesToCache,
        timestamp: Date.now()
      }));
    } catch (error) {
      logger.error('Error saving files to localStorage:', error);
    }
  }, []);

  // Quota cache helpers
  const loadCachedQuota = useCallback(() => {
    try {
      const cachedQuotaData = localStorage.getItem(QUOTA_CACHE_KEY);
      if (cachedQuotaData) {
        const { quota, timestamp } = JSON.parse(cachedQuotaData);
        const now = Date.now();
        if (now - timestamp < METADATA_CACHE_TTL) {
          setQuotaInfo(quota);
          return true;
        }
      }
    } catch (error) {
      logger.error('Error loading cached quota:', error);
    }
    return false;
  }, []);

  const saveQuotaToCache = useCallback((quotaToCache: any) => {
    try {
      localStorage.setItem(QUOTA_CACHE_KEY, JSON.stringify({
        quota: quotaToCache,
        timestamp: Date.now()
      }));
    } catch (error) {
      logger.error('Error saving quota to localStorage:', error);
    }
  }, []);

  // Fetch quota info - no dependencies on state that changes frequently
  const fetchQuotaInfo = useCallback(async () => {
    if (!user) return;
    
    try {
      setQuotaLoading(true);
      const loadingTimer = window.setTimeout(() => {
        setShowQuotaLoading(true);
      }, 300);
      quotaLoadingTimerRef.current = loadingTimer as unknown as number;
      
      const quotaInfo = await fileService.getStorageQuota();
      
      if (quotaLoadingTimerRef.current) {
        window.clearTimeout(quotaLoadingTimerRef.current);
        quotaLoadingTimerRef.current = null;
      }
      
      setQuotaInfo(quotaInfo);
      saveQuotaToCache(quotaInfo);
    } catch (error) {
      logger.error('Error in fetchQuotaInfo:', error);
    } finally {
      setQuotaLoading(false);
      setShowQuotaLoading(false);
    }
  }, [user, saveQuotaToCache]);

  // Main fetch function with stable dependencies
  const fetchFiles = useCallback(async (forceRefresh = false, backgroundFetch = false) => {
    if (!user) return;
    
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime.current;
    
    // Skip fetch if not forced and we fetched recently - use ref instead of files.length state
    if (!forceRefresh && timeSinceLastFetch < MIN_FETCH_INTERVAL && currentFilesLength.current > 0) {
      logger.debug(`Skipping fetch, last fetch was ${timeSinceLastFetch}ms ago`);
      return;
    }
    
    const startTime = performance.now();
    lastFetchTime.current = now;
    
    try {
      // Set loading state only if not a background fetch
      if (!backgroundFetch) {
        setLoading(true);
        
        const loadingTimer = window.setTimeout(() => {
          setShowLoading(true);
        }, 300);
        
        loadingTimerRef.current = loadingTimer as unknown as number;
      }
      
      const response = await fileService.listFiles({
        sortBy: sortField,
        sortDirection: sortOrder,
        filters: {
          searchQuery: debouncedSearchQuery || undefined
        }
      });

      // Clear the loading timer
      if (loadingTimerRef.current) {
        window.clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }

      // Convert FileMetadata to FileWithThumbnail
      const filesWithThumbnail: FileWithThumbnail[] = response.files.map(file => ({
        ...file,
        source: file.source === 'local' || file.source === 'google_drive' 
          ? file.source 
          : 'local', // Default to local for other sources
        usage: file.usage || {
          type: 'other',
          context: 'other'
        }
      }));

      lastFilesResponse.current = filesWithThumbnail;
      currentFilesLength.current = filesWithThumbnail.length;
      setFiles(filesWithThumbnail);
      isDataFreshRef.current = true;
      
      // Cache the files
      saveFilesToCache(filesWithThumbnail);
      
      // Use ref comparison instead of the state value
      if (forceRefresh || response.files.length !== currentFilesLength.current) {
        fetchQuotaInfo();
      }
      
      // Track feature usage
      trackFeatureUsage('files_fetch', 'completed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        count: response.files.length
      });
      
    } catch (error) {
      logger.error('Error in fetchFiles:', error);
      
      if (forceRefresh || currentFilesLength.current === 0) {
        showToast('Failed to load files', 'error');
      }
      
      // Track error
      trackFeatureUsage('files_fetch', 'failed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
      setShowLoading(false);
    }
  }, [
    user, 
    debouncedSearchQuery, 
    sortField, 
    sortOrder,
    saveFilesToCache,
    fetchQuotaInfo,
    showToast,
    trackFeatureUsage
  ]);

  // Update refs when files state changes
  useEffect(() => {
    currentFilesLength.current = files.length;
    filesForCleanup.current = files;
  }, [files]);

  // Effects for initial loading
  useEffect(() => {
    if (user?.id && !didInitialFetch.current) {
      didInitialFetch.current = true;
      
      // Track page view
      trackFeatureUsage('files_page', 'viewed');
      
      // Start journey
      const journeyId = `files_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      journeyRef.current = journeyId;
      
      trackUserJourney('files_management_started', {
        journey_id: journeyId,
        entry_point: 'direct',
        has_files: initialFiles.length > 0
      });
      
      // Handle initial data loading
      if (hasInitialSSRData.current && initialFiles.length > 0) {
        // Use SSR data
        const filesWithThumbnail = initialFiles.map(file => ({
          ...file,
          source: file.source === 'local' || file.source === 'google_drive' 
            ? file.source : 'local',
          usage: file.usage || { type: 'other', context: 'other' }
        }));
        
        setFiles(filesWithThumbnail);
        lastFilesResponse.current = filesWithThumbnail;
        isDataFreshRef.current = true;
        saveFilesToCache(filesWithThumbnail);
      } else {
        // Try cache first
        const hasCachedFiles = loadCachedFiles();
        
        // If no cache, fetch fresh data
        if (!hasCachedFiles) {
          // Delay the fetch to avoid potential race conditions
          setTimeout(() => fetchFiles(false), 0);
        } else {
          // Schedule background refresh for cache
          setTimeout(() => fetchFiles(false, true), 100);
        }
      }
      
      // Handle quota info
      if (initialQuotaInfo) {
        setQuotaInfo(initialQuotaInfo);
      } else {
        const hasCachedQuota = loadCachedQuota();
        if (!hasCachedQuota) {
          fetchQuotaInfo();
        }
      }
    }
    
    // Cleanup function
    return () => {
      // Clear timers
      if (loadingTimerRef.current) {
        window.clearTimeout(loadingTimerRef.current);
      }
      if (quotaLoadingTimerRef.current) {
        window.clearTimeout(quotaLoadingTimerRef.current);
      }
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      
      // End journey
      if (journeyRef.current) {
        trackUserJourney('files_management_completed', {
          journey_id: journeyRef.current,
          outcome: 'journey_complete'
        });
      }
      
      // Clean up blob URLs from ref instead of files state
      filesForCleanup.current.forEach(file => {
        if (file.thumbnailUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(file.thumbnailUrl);
        }
      });
    };
  }, [
    user?.id, 
    initialFiles, 
    initialQuotaInfo,
    loadCachedFiles,
    loadCachedQuota,
    saveFilesToCache,
    fetchFiles,
    fetchQuotaInfo,
    trackFeatureUsage,
    trackUserJourney
    // files removed from dependency array
  ]);

  // Separate effect for view settings
  useEffect(() => {
    if (settings?.files_view_mode === 'grid' || settings?.files_view_mode === 'list') {
      setViewType(settings.files_view_mode);
    }
  }, [settings?.files_view_mode]);

  // Handle client-side filtering and sorting when search changes
  useEffect(() => {
    // Skip if no data or no user
    if (!user?.id || !lastFilesResponse.current.length) return;
    
    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Set new timeout to prevent rapid updates
    searchTimeout.current = setTimeout(() => {
      // Try client-side filtering
      const filtered = lastFilesResponse.current.filter(file =>
        file.file_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
      
      if (filtered.length > 0) {
        // Apply sort
        const sorted = sortFiles(filtered);
        
        // Only update state if the sorted array is different from current files
        // Use JSON.stringify for deep comparison to avoid unnecessary re-renders
        const currentFilesJSON = JSON.stringify(files.map(f => f.id));
        const sortedFilesJSON = JSON.stringify(sorted.map(f => f.id));
        
        if (currentFilesJSON !== sortedFilesJSON) {
          setFiles(sorted);
        }
      } else if (debouncedSearchQuery) {
        // If no matches found locally and we have a search term, fetch from server
        fetchFiles(false);
      }
    }, 0);
    
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [debouncedSearchQuery, sortField, sortOrder, user?.id]);

  // Clickoutside for sort dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add escape key handler for side panel
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedFileForInfo) {
        setSelectedFileForInfo(null);
      }
    };

    if (selectedFileForInfo) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [selectedFileForInfo]);

  // Wrap sortFiles with useCallback
  const sortFiles = useCallback((files: FileWithThumbnail[]): FileWithThumbnail[] => {
    return [...files].sort((a, b) => {
      let result = 0;
      switch (sortField) {
        case 'file_name':
          result = a.file_name.localeCompare(b.file_name);
          break;
        
        case 'created_at':
          result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        
        case 'mime_type':
          result = a.mime_type.localeCompare(b.mime_type);
          break;
        
        case 'size': {
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          result = sizeA - sizeB;
          break;
        }
        
        case 'usage': {
          const usageA = a.usage?.context || '';
          const usageB = b.usage?.context || '';
          result = usageA.localeCompare(usageB);
          break;
        }
      }
      return sortOrder === 'asc' ? result : -result;
    });
  }, [sortField, sortOrder]);

  const handleDownload = useCallback(async (file: FileWithThumbnail) => {
    const startTime = performance.now();
    
    try {
      const url = await fileService.getFileUrl(file);
      
      // For Google Drive files, we need to handle differently
      if (file.source === 'google_drive') {
        // Google Drive files need to open in new tab due to their auth requirements
        window.open(url, '_blank');
        
        // Track feature usage
        trackFeatureUsage('file_download', 'completed', {
          journey_id: journeyRef.current,
          duration_ms: Math.round(performance.now() - startTime),
          success: true,
          file_type: file.mime_type,
          file_size: file.size,
          source: 'google_drive'
        });
        
        return;
      }

      // For local files, create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Track feature usage
      trackFeatureUsage('file_download', 'completed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        file_type: file.mime_type,
        file_size: file.size,
        source: 'local'
      });
      
    } catch (error) {
      logger.error('Error downloading file:', error);
      showToast('Failed to download file', 'error');
      
      // Track feature usage
      trackFeatureUsage('file_download', 'failed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        file_type: file.mime_type,
        file_size: file.size
      });
    }
  }, [fileService, showToast, trackFeatureUsage]);

  const handleDelete = useCallback(async (file: FileWithThumbnail) => {
    const startTime = performance.now();
    
    try {
      await fileService.deleteFile(file);
      showToast('File deleted successfully', 'success');
      fetchFiles(true); // Force refresh the file list
      
      // Track feature usage
      trackFeatureUsage('file_delete', 'completed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        file_id: file.id,
        file_type: file.mime_type
      });
      
    } catch (error) {
      logger.error('Error deleting file:', error);
      showToast('Failed to delete file', 'error');
      
      // Track feature usage
      trackFeatureUsage('file_delete', 'failed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        file_id: file.id,
        file_type: file.mime_type
      });
    }
  }, [fileService, fetchFiles, showToast, trackFeatureUsage]);

  const handleView = useCallback(async (file: FileWithThumbnail) => {
    const startTime = performance.now();
    
    try {
      // For Google Drive files, open directly in a new tab
      if (file.source === 'google_drive') {
        const url = await fileService.getFileUrl(file);
        window.open(url, '_blank');
        
        // Track feature usage
        trackFeatureUsage('file_view', 'completed', {
          journey_id: journeyRef.current,
          duration_ms: Math.round(performance.now() - startTime),
          success: true,
          file_type: file.mime_type,
          file_size: file.size,
          source: 'google_drive'
        });
        
        trackUserJourney('file_viewed', {
          journey_id: journeyRef.current,
          file_id: file.id,
          source: 'google_drive'
        });
        
        return;
      }
      
      // For local files, use the modal
      const url = await fileService.getFileUrl(file);
      setSelectedResume(file);
      setSelectedResumeUrl(url);
      setIsResumeModalOpen(true);
      
      // Track feature usage
      trackFeatureUsage('file_view', 'completed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        file_type: file.mime_type,
        file_size: file.size,
        source: 'local'
      });
      
      trackUserJourney('file_viewed', {
        journey_id: journeyRef.current,
        file_id: file.id,
        source: 'local'
      });
      
    } catch (error) {
      logger.error('Error viewing file:', error);
      showToast('Failed to view file', 'error');
      
      // Track feature usage
      trackFeatureUsage('file_view', 'failed', {
        journey_id: journeyRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        file_type: file.mime_type,
        file_size: file.size
      });
    }
  }, [fileService, showToast, setSelectedResume, setSelectedResumeUrl, setIsResumeModalOpen, trackFeatureUsage, trackUserJourney]);

  const handleFileClick = useCallback((file: FileWithThumbnail) => {
    setSelectedFileForInfo(file);
  }, []);

  const handleViewModeChange = useCallback(async (mode: 'grid' | 'list') => {
    setViewType(mode);
    
    // Track feature usage
    trackFeatureUsage('files_view_mode', 'changed', {
      journey_id: journeyRef.current,
      previous_mode: viewType,
      new_mode: mode
    });
    
    // Update user settings
    await updateSettings({ files_view_mode: mode });
  }, [viewType, updateSettings, trackFeatureUsage]);

  // Helper function to determine number of columns based on screen width
  const getColumnsPerRow = useCallback(() => {
    const width = window.innerWidth;
    if (width >= 1536) return 8; // 2xl
    if (width >= 1280) return 6; // xl
    if (width >= 1024) return 4; // lg
    if (width >= 768) return 3;  // md
    if (width >= 640) return 2;  // sm
    return 1; // default
  }, []);

  // Re-add the filteredFiles useMemo
  const filteredFiles = useMemo(() => {
    // Use debounced search query for consistency with server requests
    if (!files.length) return [];
    
    // Skip filtering if no search query
    if (!searchQuery) return sortFiles(files);
    
    const filtered = files.filter(file =>
      file.file_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortFiles(filtered);
  }, [files, searchQuery, sortFiles]);

  // Add keyboard navigation handler with useCallback
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>, currentFile: FileWithThumbnail) => {
    const currentIndex = filteredFiles.findIndex(file => file.id === currentFile.id);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = Math.min(currentIndex + 1, filteredFiles.length - 1);
        event.preventDefault();
        break;
      case 'ArrowLeft':
        nextIndex = Math.max(currentIndex - 1, 0);
        event.preventDefault();
        break;
      case 'ArrowDown':
        if (viewType === 'list') {
          nextIndex = Math.min(currentIndex + 1, filteredFiles.length - 1);
        } else {
          // For grid view, move down by the number of columns
          const columnsPerRow = getColumnsPerRow();
          nextIndex = Math.min(currentIndex + columnsPerRow, filteredFiles.length - 1);
        }
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (viewType === 'list') {
          nextIndex = Math.max(currentIndex - 1, 0);
        } else {
          // For grid view, move up by the number of columns
          const columnsPerRow = getColumnsPerRow();
          nextIndex = Math.max(currentIndex - columnsPerRow, 0);
        }
        event.preventDefault();
        break;
      case 'Enter':
      case ' ':
        handleFileClick(currentFile);
        event.preventDefault();
        break;
      default:
        return;
    }

    const nextFile = filteredFiles[nextIndex];
    if (nextFile) {
      setFocusedFileId(nextFile.id);
      fileRefs.current[nextFile.id]?.focus();
    }
  }, [filteredFiles, viewType, handleFileClick, getColumnsPerRow, setFocusedFileId]);

  const { isBottomUrlBarVisible, isMobile } = useBottomUrlBarDetection();
  
  // Calculate the bottom spacer height based on URL bar visibility
  const bottomSpacerHeight = useMemo(() => {
    // Only add padding when on mobile AND URL bar is visible
    if (isMobile && isBottomUrlBarVisible) {
      // URL bar is showing, add significant padding (160px is typically enough for iOS Safari)
      return 160;
    }
    // Standard padding for all other cases
    return 24;
  }, [isMobile, isBottomUrlBarVisible]);

  // Add scroll handler to help hide URL bar on iOS
  useEffect(() => {
    // Only apply this on iOS devices
    const isIOS = /iphone|ipod|ipad/.test(navigator.userAgent.toLowerCase());
    if (!isIOS) return;

    const handleScroll = () => {
      // After scrolling a bit, force the window to scroll slightly more
      // This triggers Safari to hide the URL bar
      if (window.scrollY > 50) {
        // This timeout allows the natural scroll to finish first
        setTimeout(() => {
          window.scrollBy(0, 1);
        }, 100);
      }
    };

    // Throttle the scroll handler to avoid performance issues
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledScroll = () => {
      if (scrollTimeout === null) {
        scrollTimeout = setTimeout(() => {
          handleScroll();
          scrollTimeout = null;
        }, 200);
      }
    };

    window.addEventListener('scroll', throttledScroll);
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  // Function to hide URL bar on iOS devices
  const hideUrlBar = useCallback(() => {
    // Only for iOS devices
    const isIOS = /iphone|ipod|ipad/.test(navigator.userAgent.toLowerCase());
    if (!isIOS || !isMobile) return;
    
    // First scroll to 1px to trigger the address bar hiding
    window.scrollTo(0, 1);
    
    // Then add a small additional scroll after a short delay
    setTimeout(() => {
      window.scrollBy(0, 1);
    }, 100);
  }, [isMobile]);

  // Attempt to hide URL bar on initial load
  useEffect(() => {
    if (isMobile) {
      // Wait for component to fully render
      const timer = setTimeout(() => {
        hideUrlBar();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isMobile, hideUrlBar]);

  // Also try to hide URL bar after loading files
  useEffect(() => {
    if (files.length > 0 && isMobile && !loading) {
      hideUrlBar();
    }
  }, [files.length, isMobile, loading, hideUrlBar]);

  // Handle opening file from location state (when navigating from Timeline)
  useEffect(() => {
    // Skip if we've already processed this file ID or if no file ID is provided
    if (!openFileId || !shouldOpenFile || processedFileIdRef.current === openFileId || loading) {
      return;
    }

    // Set the ref to track that we've processed this file ID
    processedFileIdRef.current = openFileId;

    // Wait for files to be loaded
    if (files.length > 0) {
      // Find the matching file by ID
      const fileToOpen = files.find(file => file.id === openFileId);

      // If file is found, open the details panel
      if (fileToOpen) {
        setSelectedFileForInfo(fileToOpen);
        
        // Log the file opening
        logger.info('Opening file details from timeline navigation:', openFileId);
        
        // Track usage
        trackFeatureUsage('file_open_from_timeline', 'opened', {
          journey_id: journeyRef.current,
          file_id: openFileId,
          file_type: fileToOpen.mime_type
        });
      } else {
        // If we can't find the file, show a message
        showToast('File not found or has been removed', 'error');
        logger.warn('File from timeline navigation not found:', openFileId);
      }
    }
  }, [openFileId, shouldOpenFile, files, loading, showToast]);

  // Hide URL bar when user returns to the page
  useEffect(() => {
    if (!isMobile) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to ensure the browser has time to fully restore the page
        setTimeout(hideUrlBar, 300);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also hide URL bar when window is focused
    window.addEventListener('focus', hideUrlBar);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', hideUrlBar);
    };
  }, [isMobile, hideUrlBar]);

  if (isOffline) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <p className="text-gray-600">
          You are currently offline. File management features are not available.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader title="Files" />
      <div 
        ref={scrollContainerRef}
        className="h-full flex-1 overflow-y-auto"
        style={{ paddingTop: `${PAGE_HEADER_HEIGHT}px` }}
      >
        <div className="p-4 w-full">
          <div className="max-w-[1920px] mx-auto">
            {/* Responsive layout for mobile devices */}
            <div className="flex flex-col md:hidden space-y-4 mb-6">
              {/* First row: Search and Refresh */}
              <div className="flex justify-between items-center gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by file name"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      darkMode 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <button
                  onClick={() => {
                    fetchFiles(true);
                  }}
                  className={`px-3 py-2 rounded-lg border text-sm whitespace-nowrap ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Refresh
                </button>
              </div>
              
              {/* Second row: Sort dropdown and view toggles */}
              <div className="flex justify-between items-center">
                <div className="relative" ref={sortRef}>
                  <button
                    onClick={() => setIsSortOpen(!isSortOpen)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                        : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span>Sort: {getSortLabel(sortField)}</span>
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                  
                  {isSortOpen && (
                    <div className={`absolute left-0 mt-2 w-48 rounded-md shadow-lg z-10 ${
                      darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                    }`}>
                      <div className="py-1">
                        {SORT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              if (sortField === option.value) {
                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField(option.value as SortField);
                                setSortOrder('desc');
                              }
                              setIsSortOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              darkMode
                                ? 'hover:bg-gray-700 text-gray-200'
                                : 'hover:bg-gray-100 text-gray-700'
                            } ${sortField === option.value ? 'font-semibold' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{option.label}</span>
                              {sortField === option.value && (
                                <span className="text-xs">
                                  {sortOrder === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewModeChange('grid')}
                    className={`p-2 rounded ${
                      viewType === 'grid' 
                        ? 'bg-blue-500 text-white' 
                        : darkMode 
                          ? 'text-gray-400 hover:text-white' 
                          : 'text-gray-600 hover:text-gray-900'
                    }`}
                    aria-label="Grid view"
                    title="Grid view"
                  >
                    <Squares2X2Icon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleViewModeChange('list')}
                    className={`p-2 rounded ${
                      viewType === 'list' 
                        ? 'bg-blue-500 text-white' 
                        : darkMode 
                          ? 'text-gray-400 hover:text-white' 
                          : 'text-gray-600 hover:text-gray-900'
                    }`}
                    aria-label="List view"
                    title="List view"
                  >
                    <ListBulletIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop layout - only visible on md and larger screens */}
            <div className="hidden md:flex justify-between items-center mb-6 gap-4">
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search by file name"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-700 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              
              <button
                onClick={() => {
                  fetchFiles(true);
                }}
                className={`px-4 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 hover:bg-gray-50'
                }`}
              >
                Refresh
              </button>
              
              <div className="relative mx-4" ref={sortRef}>
                <button
                  onClick={() => setIsSortOpen(!isSortOpen)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span>Sort by: {getSortLabel(sortField)}</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
                
                {isSortOpen && (
                  <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                    darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}>
                    <div className="py-1">
                      {SORT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            if (sortField === option.value) {
                              setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField(option.value as SortField);
                              setSortOrder('desc');
                            }
                            setIsSortOpen(false);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            darkMode
                              ? 'hover:bg-gray-700 text-gray-200'
                              : 'hover:bg-gray-100 text-gray-700'
                          } ${sortField === option.value ? 'font-semibold' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{option.label}</span>
                            {sortField === option.value && (
                              <span className="text-xs">
                                {sortOrder === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleViewModeChange('grid')}
                  className={`p-2 rounded ${
                    viewType === 'grid' 
                      ? 'bg-blue-500 text-white' 
                      : darkMode 
                        ? 'text-gray-400 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-label="Grid view"
                  title="Grid view"
                >
                  <Squares2X2Icon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleViewModeChange('list')}
                  className={`p-2 rounded ${
                    viewType === 'list' 
                      ? 'bg-blue-500 text-white' 
                      : darkMode 
                        ? 'text-gray-400 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-label="List view"
                  title="List view"
                >
                  <ListBulletIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Files Grid/List */}
            {showLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="text-center py-12">
                <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-gray-500">No files found</p>
                <p className="text-sm text-gray-400">Upload files from the Resumes</p>
              </div>
            ) : viewType === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
                {filteredFiles.map((file) => (
                  <div
                    key={file.id}
                    ref={el => { fileRefs.current[file.id] = el; }}
                    onClick={() => handleFileClick(file)}
                    onKeyDown={(e) => handleKeyDown(e, file)}
                    className={`p-2 rounded-lg ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    } shadow hover:shadow-md transition-shadow group relative cursor-pointer ${
                      focusedFileId === file.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    tabIndex={0}
                  >
                    <ThumbnailPreview
                      file={file}
                      size="lg"
                      className="mb-2"
                    />
                    <div className="text-xs">
                      <p className="font-medium truncate">{file.file_name}</p>
                      <p className="text-gray-500 text-[10px]">
                        {formatFileSize(file.size)} • {file.usage?.context}
                      </p>
                    </div>
                    {/* Action buttons */}
                    <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      {(file.mime_type === 'application/pdf' || file.source === 'google_drive') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(file);
                          }}
                          className="p-1 text-gray-500 hover:text-blue-500"
                          title={file.source === 'google_drive' ? "Open in Google Drive (new tab)" : "View"}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file);
                        }}
                        className="p-1 text-gray-500 hover:text-blue-500"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(file);
                        }}
                        className="text-red-500 hover:text-red-600"
                        aria-label="Delete file"
                        title={file.source === 'google_drive' 
                          ? "This will remove the file reference from Career Card but won't delete it from Google Drive" 
                          : "Delete file"
                        }
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                {filteredFiles.map((file) => (
                  <div
                    key={file.id}
                    ref={el => { fileRefs.current[file.id] = el; }}
                    onClick={() => handleFileClick(file)}
                    onKeyDown={(e) => handleKeyDown(e, file)}
                    className={`flex items-center p-4 ${
                      darkMode 
                        ? 'border-gray-700 hover:bg-gray-700' 
                        : 'border-gray-200 hover:bg-gray-50'
                    } border-b last:border-b-0 group cursor-pointer ${
                      focusedFileId === file.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    tabIndex={0}
                  >
                    <ThumbnailPreview
                      file={file}
                      size="sm"
                      className="mr-4 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{file.file_name}</p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(file.size)} • {file.usage?.context} • {formatDate(file.created_at, settings?.timezone)}
                      </p>
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {(file.mime_type === 'application/pdf' || file.source === 'google_drive') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(file);
                          }}
                          className="p-2 text-gray-500 hover:text-blue-500"
                          title={file.source === 'google_drive' ? "Open in Google Drive (new tab)" : "View"}
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file);
                        }}
                        className="p-2 text-gray-500 hover:text-blue-500"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(file);
                        }}
                        className="text-red-500 hover:text-red-600"
                        aria-label="Delete file"
                        title={file.source === 'google_drive' 
                          ? "This will remove the file reference from Career Card but won't delete it from Google Drive" 
                          : "Delete file"
                        }
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Bottom spacer to avoid URL bar overlap on mobile */}
        <div 
          style={{ 
            height: bottomSpacerHeight,
            marginTop: 20,
            transition: 'height 0.3s ease-in-out' 
          }} 
          className="w-full bg-transparent"
          aria-hidden="true"
        />
      </div>

      {selectedResume && selectedResumeUrl && (
        <FileViewerModal
          isOpen={isResumeModalOpen}
          onClose={() => setIsResumeModalOpen(false)}
          fileUrl={selectedResumeUrl}
          mimeType={selectedResume.mime_type}
        />
      )}

      {selectedFileForInfo && (
        <div className={`fixed right-0 top-0 h-full w-80 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-l shadow-xl transform transition-transform duration-200 ease-in-out z-40`}>
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">File Information</h3>
              <button
                onClick={() => setSelectedFileForInfo(null)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close panel"
                title="Close panel"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Preview */}
              <div className="flex justify-center">
                <ThumbnailPreview
                  file={selectedFileForInfo}
                  size="lg"
                  className="border dark:border-gray-700"
                />
              </div>

              {/* File Details */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Name</h4>
                  <p className="text-sm break-words">{selectedFileForInfo.file_name}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Type</h4>
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-1 text-gray-400" />
                    <p className="text-sm">{selectedFileForInfo.mime_type}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Location</h4>
                  <div className="flex items-center">
                    <FolderIcon className="w-4 h-4 mr-1 text-gray-400" />
                    <p className="text-sm">{selectedFileForInfo.usage?.context}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Added</h4>
                  <div className="flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                    <p className="text-sm">{formatDate(selectedFileForInfo.created_at, settings?.timezone)}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Size</h4>
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-1 text-gray-400" />
                    <p className="text-sm">{formatFileSize(selectedFileForInfo.size)}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                {(selectedFileForInfo.mime_type === 'application/pdf' || selectedFileForInfo.source === 'google_drive') && (
                  <button
                    onClick={() => handleView(selectedFileForInfo)}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors"
                  >
                    {selectedFileForInfo.source === 'google_drive' ? 'Open in Google Drive' : 'View File'}
                  </button>
                )}
                <button
                  onClick={() => handleDownload(selectedFileForInfo)}
                  className="w-full px-4 py-2 text-sm font-medium text-blue-500 border border-blue-500 rounded hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Download
                </button>
                <button
                  onClick={() => {
                    handleDelete(selectedFileForInfo);
                    setSelectedFileForInfo(null);
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-red-500 border border-red-500 rounded hover:bg-red-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fixed position quota display - moved inside scrollable area with bottom padding */}
      {quotaInfo && (
        <StorageQuotaDisplay 
          usagePercentage={quotaInfo.usagePercentage} 
          usedSize={quotaInfo.usedSize} 
          totalSize={quotaInfo.totalSize} 
          loading={showQuotaLoading}
        />
      )}
    </div>
  );
};

export default Files; 